import React, { useMemo } from 'react';
import { 
  LayoutGrid, 
  Sparkles, 
  User2, 
  X, 
  Target,
  Flag,
  Calendar,
  MessageSquare,
  ShieldCheck,
  Zap,
  Clock,
  IterationCcw,
  Eye,
  Users,
  Lightbulb
} from 'lucide-react';
import PremiumDropdown, { DropdownOption } from './PremiumDropdown';

interface GlobalKanbanFiltersProps {
  filterAssignee: string;
  setFilterAssignee: (val: string) => void;
  filterStatus: string;
  setFilterStatus: (val: string) => void;
  filterType: string;
  setFilterType: (val: string) => void;
  allAssignees: string[];
  availableTypes: string[];
  availableStatuses: string[];
}

const GlobalKanbanFilters: React.FC<GlobalKanbanFiltersProps> = ({
  filterAssignee,
  setFilterAssignee,
  filterStatus,
  setFilterStatus,
  filterType,
  setFilterType,
  allAssignees,
  availableTypes,
  availableStatuses
}) => {
  const typeOptions = useMemo(() => {
    const baseOptions: DropdownOption[] = [
      { value: 'task', label: 'Aufgaben', icon: <Target size={13} />, color: 'bg-blue-500' },
      { value: 'milestone', label: 'Meilensteine', icon: <Flag size={13} />, color: 'bg-amber-500' },
      { value: 'deadline', label: 'Fristen', icon: <Clock size={13} />, color: 'bg-rose-600' },
      { value: 'meeting', label: 'Meetings', icon: <Calendar size={13} />, color: 'bg-indigo-500' },
      { value: 'decision', label: 'Entscheidungen', icon: <MessageSquare size={13} />, color: 'bg-rose-500' },
      { value: 'approval', label: 'Freigaben', icon: <ShieldCheck size={13} />, color: 'bg-emerald-500' },
      { value: 'workshop', label: 'Workshops', icon: <Zap size={13} />, color: 'bg-amber-400' },
      { value: 'iteration', label: 'Iterationen', icon: <IterationCcw size={13} />, color: 'bg-indigo-400' },
      { value: 'review', label: 'Reviews', icon: <Eye size={13} />, color: 'bg-slate-500' },
      { value: 'cooperation', label: 'Kooperationen', icon: <Users size={13} />, color: 'bg-blue-400' },
      { value: 'contact', label: 'Kontakte', icon: <User2 size={13} />, color: 'bg-amber-500' },
      { value: 'idea', label: 'Ideen', icon: <Lightbulb size={13} />, color: 'bg-yellow-500' },
    ];

    // Mapping for German labels supporting both singular and plural forms
    const germanToValue: Record<string, string> = {
      'Aufgabe': 'task',
      'Aufgaben': 'task',
      'Meilenstein': 'milestone',
      'Meilensteine': 'milestone',
      'Deadline': 'deadline',
      'Frist': 'deadline',
      'Fristen': 'deadline',
      'Meeting': 'meeting',
      'Meetings': 'meeting',
      'Entscheidung': 'decision',
      'Entscheidungen': 'decision',
      'Freigabe': 'approval',
      'Freigaben': 'approval',
      'Workshop': 'workshop',
      'Workshops': 'workshop',
      'Iteration': 'iteration',
      'Iterationen': 'iteration',
      'Review': 'review',
      'Reviews': 'review',
      'Kooperation': 'cooperation',
      'Kooperationen': 'cooperation',
      'Kontakt': 'contact',
      'Kontakte': 'contact',
      'Idea': 'idea',
      'Idee': 'idea',
      'Ideen': 'idea'
    };

    const finalOptions: DropdownOption[] = [{ value: 'all', label: 'Alle', icon: <LayoutGrid size={13} /> }];
    
    // Add all base options first (ensures they are always selectable)
    baseOptions.forEach(opt => {
      finalOptions.push(opt);
    });

    // Add any dynamic types found in data that aren't in base
    const baseValues = new Set(baseOptions.map(o => o.value));
    availableTypes.forEach(t => {
      if (!t) return;
      const val = germanToValue[t] || t.toLowerCase();
      if (!baseValues.has(val)) {
        finalOptions.push({
          value: t,
          label: t.charAt(0).toUpperCase() + t.slice(1),
          icon: <LayoutGrid size={13} />,
          color: 'bg-slate-400'
        });
      }
    });

    return [
      finalOptions[0],
      ...finalOptions.slice(1).sort((a, b) => a.label.localeCompare(b.label))
    ];
  }, [availableTypes]);

  const statusOptions = useMemo(() => {
    return [
      { value: 'all', label: 'Alle Status', icon: <Sparkles size={13} /> },
      { value: 'open', label: 'Offen', color: 'bg-slate-400' },
      { value: 'timer', label: 'Zeit (Aktiv/Erfasst)', color: 'bg-indigo-500' },
      { value: 'hold', label: 'Wartend', color: 'bg-amber-500' },
      { value: 'blocked', label: 'Blockiert', color: 'bg-rose-500' },
      { value: 'completed', label: 'Erledigt', color: 'bg-emerald-600' }
    ];
  }, []);

  const assigneeOptions: DropdownOption[] = [
    { value: 'all', label: 'Alle Personen', icon: <User2 size={13} /> },
    ...allAssignees.map(a => ({
      value: a,
      label: a,
      icon: (
        <div className="w-4 h-4 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-[7px] font-black text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
          {a.substring(0, 2).toUpperCase()}
        </div>
      )
    }))
  ];

  const hasActiveFilters = filterAssignee !== 'all' || filterStatus !== 'all' || filterType !== 'all';

  return (
    <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-200/50 dark:border-slate-800 shadow-inner group/filters relative h-[34px]">
      <PremiumDropdown
        value={filterType}
        options={typeOptions}
        onChange={setFilterType}
        icon={<LayoutGrid size={13} />}
        alignRight={true}
        showLabel={true}
      />
      
      <div className="w-px h-3 bg-slate-200 dark:bg-slate-700 mx-0.5" />

      <PremiumDropdown
        value={filterStatus}
        options={statusOptions}
        onChange={setFilterStatus}
        icon={<Sparkles size={13} />}
        alignRight={true}
        showSearch={statusOptions.length > 8}
      />

      <div className="w-px h-3 bg-slate-200 dark:bg-slate-700 mx-0.5" />

      <PremiumDropdown
        value={filterAssignee}
        options={assigneeOptions}
        onChange={setFilterAssignee}
        icon={<User2 size={13} />}
        showSearch={allAssignees.length > 5}
        alignRight={true}
      />

      {hasActiveFilters && (
        <button
          onClick={() => {
            setFilterAssignee('all');
            setFilterStatus('all');
            setFilterType('all');
          }}
          className="ml-0.5 p-1 bg-rose-50 hover:bg-rose-100 dark:bg-rose-900/20 dark:hover:bg-rose-900/40 text-rose-600 dark:text-rose-400 rounded-lg transition-all active:scale-95 group/reset"
          title="Filter zurücksetzen"
        >
          <X size={12} className="group-hover/reset:rotate-90 transition-transform duration-300" />
        </button>
      )}
    </div>
  );
};

export default GlobalKanbanFilters;
